import React from 'react';
import { getLanguageValue } from 'locales/i18n';
import { PAGE_TYPE } from 'enums';
import { getImageUrl } from 'helpers/Image.helper';
import LoadingComponent from 'commons/loading/Loading.component';
import { IAgencyList } from 'api/interfaces/app.interface';

interface AgencyIndexComponentProps {
    agencyList: IAgencyList[];
    isLoading: boolean;
    isLoadingMore: boolean;
    hasMore: boolean;
    onLoadMore: () => void;
}

const AgencyIndexComponent: React.FC<AgencyIndexComponentProps> = ({
    agencyList,
    isLoading,
    isLoadingMore,
    hasMore,
    onLoadMore
}) => {

    return (
        <main className="bg-gray-50 min-h-screen">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-6">
                <h1 className="text-3xl font-bold text-gray-900 mb-6">
                    {getLanguageValue(PAGE_TYPE.STUDENT, "hubAgency.txtTitle")}
                </h1>

                <div className="max-w-4xl mx-auto">
                    {isLoading ? (
                        <div className="flex justify-center items-center py-12">
                            <LoadingComponent />
                        </div>
                    ) : agencyList.length === 0 ? (
                        <div className="bg-white rounded-lg shadow-sm p-6 text-center">
                            <div className="flex flex-col items-center justify-center text-gray-400">
                                <svg className="w-12 h-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                                <p className="text-sm">
                                    {getLanguageValue(PAGE_TYPE.STUDENT, "hubAgency.txtNoData")}
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {agencyList.map((agency) => (
                                    <div 
                                        key={agency.id} 
                                        className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow"
                                    >
                                        <div className="p-4 sm:p-5">
                                            <div className="flex items-start space-x-4 sm:space-x-5">
                                                {/* 기획사 로고 - 다양한 비율 지원 */}
                                                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0 flex items-center justify-center p-2">
                                                    <img 
                                                        src={getImageUrl(agency.logoImage, 200)} 
                                                        alt={agency.name}
                                                        className="max-w-full max-h-full w-auto h-auto object-contain"
                                                        style={{ 
                                                            maxWidth: '100%', 
                                                            maxHeight: '100%',
                                                            width: 'auto',
                                                            height: 'auto'
                                                        }}
                                                        onError={(e) => {
                                                            (e.target as HTMLImageElement).src = './imgs/icons/icon-agency-profile.svg';
                                                        }}
                                                    />
                                                </div>

                                                {/* 기획사 정보 */}
                                                <div className="flex-1 min-w-0">
                                                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1.5 sm:mb-2 truncate">
                                                        {agency.name}
                                                    </h3>
                                                    <div className="space-y-1.5">
                                                        <div className="flex items-center text-xs sm:text-sm text-gray-500">
                                                            <img 
                                                                src="./imgs/icons/icon-favorite.svg" 
                                                                alt="즐겨찾기" 
                                                                className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 flex-shrink-0"
                                                            />
                                                            <span className="mr-1.5">{getLanguageValue(PAGE_TYPE.STUDENT, "hubAgency.txtFavorite")}</span>
                                                            <span>{agency.currentFavoriteCount} / {agency.totalFavoriteCount}</span>
                                                        </div>
                                                        <div className="flex items-center text-xs sm:text-sm text-gray-500">
                                                            <img 
                                                                src="./imgs/icons/icon-message.svg" 
                                                                alt="캐스팅 채팅" 
                                                                className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 flex-shrink-0"
                                                            />
                                                            <span className="mr-1.5">{getLanguageValue(PAGE_TYPE.STUDENT, "hubAgency.txtCastingChat")}</span>
                                                            <span>{agency.chatCount}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* 더보기 버튼 */}
                            {hasMore && !isLoadingMore && (
                                <div className="flex justify-center py-4">
                                    <button
                                        onClick={onLoadMore}
                                        className="px-5 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg
                                            hover:bg-gray-50 transition-colors"
                                    >
                                        {getLanguageValue(PAGE_TYPE.COMMON, "btn.txtMore")}
                                    </button>
                                </div>
                            )}

                            {/* 추가 로딩 인디케이터 */}
                            {isLoadingMore && (
                                <div className="flex justify-center items-center py-6">
                                    <svg className="w-5 h-5 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                </div>
                            )}

                            {/* 더 이상 데이터가 없을 때 */}
                            {!hasMore && agencyList.length > 0 && !isLoadingMore && (
                                <div className="flex justify-center items-center py-6">
                                    <div className="text-center text-gray-400 text-xs">
                                        {getLanguageValue(PAGE_TYPE.STUDENT, "hubAgency.txtNoMoreData")}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </main>
    );
};

export default AgencyIndexComponent;

